import dayjs from 'dayjs';
import React from "react";
import PopupNavBar from "components/Layout/PopupNavBar";
import { useCallback, useEffect, useState } from "react";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { fetchApi, fetchApiFormData } from "services/api";
import { Title } from "utils/title";
import UserPerformanceGoalDialog from "./UserPerformanceGoalDialog";
import UserWorkScheduleDialog from "./UserWorkScheduleDialog";
import { Loader } from "@progress/kendo-react-indicators";
import { Link, useParams } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { pencilIcon, calendarIcon } from "@progress/kendo-svg-icons";
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle, ChartTooltip, ChartValueAxis, ChartValueAxisItem, Sparkline } from "@progress/kendo-react-charts";
import { formatNumber } from "@progress/kendo-intl";
import SimpleDateRangePicker from "components/SimpleDateRangePicker";
import UserNoteEditUI, { NewUserPerformanceNote } from "./UserNoteEditUI";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import CustomerInput, { CustomerInputValue } from "components/CustomerInput";
import { useSessionStorage } from "usehooks-ts";
import noop from "lodash/noop";

type RouteComponentParams = {
  userId: string;
}

type UserDashboardResponse = {
  FullName: string;

  // Smaller Metrics for the top of the page
  Quotes: DataSeries[];
  CustomerTouches: DataSeries[];
  OrderRPM: DataSeries[];
  QuoteRPM: DataSeries[];
  TripPayTotal: number;
  AveragePayPerLoad: number;

  // Main Charts
  Booked: DataSeries[];
  MarginPercentage: DataSeries[];
  ConversionPercentage: DataSeries[];
}

type DataSeries = {
  Date: string | Date;
  Value: number;
}

const VehicleTypes = [
  { ID: 1, Name: 'Cargo Van' },
  { ID: 15, Name: 'Sprinter' },
  { ID: 2, Name: 'Small Straight' },
  { ID: 3, Name: 'Large Straight' },
  { ID: 4, Name: 'Tractor' },
];

const defaultCustomer: CustomerInputValue = {
  CustomerID: 0,
  CustomerNumber: ''
}

// Utility to downsample an array to a max number of points
function downsample<T>(arr: T[], maxPoints: number, valueSelector: (item: T) => number): number[] {
  if (arr.length <= maxPoints) return arr.map(valueSelector);
  const result: number[] = [];
  const bucketSize = arr.length / maxPoints;
  for (let i = 0; i < maxPoints; i++) {
    const start = Math.floor(i * bucketSize);
    const end = Math.floor((i + 1) * bucketSize);
    const bucket = arr.slice(start, end);
    if (bucket.length) {
      // Average the values in the bucket
      const avg = bucket.reduce((sum, item) => sum + valueSelector(item), 0) / bucket.length;
      result.push(avg);
    }
  }
  return result;
}

const UserReport = () => {
  const { status } = useThemeSwitcher();
  const { userId: userIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useSessionStorage<Date>(
    "UserPerformance-FromDate",
    () => dayjs().tz("America/New_York").startOf('isoWeek').toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [toDate, setToDate] = useSessionStorage<Date>(
    "UserPerformance-ToDate",
    () => dayjs().tz("America/New_York").endOf('isoWeek').startOf('day').toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [vehicleTypeIds, setVehicleTypeIDs] = useSessionStorage<number[]>("UserPerformance-VehicleTypeIDs", []);
  const [billingCustomer, setBillingCustomer] = useSessionStorage<CustomerInputValue>("UserPerformance-BillingCustomer", defaultCustomer);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editScheduleUserId, setEditScheduleUserId] = useState<number | null>(null);

  const [name, setName] = useState<string | null>(null);
  const [booked, setBooked] = useState<DataSeries[]>([]);
  const [marginPercentage, setMarginPercentage] = useState<DataSeries[]>([]);
  const [conversionPercentage, setConversionPercentage] = useState<DataSeries[]>([]);
  const [quotes, setQuotes] = useState<DataSeries[]>([]);
  const [customerTouches, setCustomerTouches] = useState<DataSeries[]>([]);
  const [orderRPM, setOrderRPM] = useState<DataSeries[]>([]);
  const [quoteRPM, setQuoteRPM] = useState<DataSeries[]>([]);
  const [tripPayTotal, setTripPayTotal] = useState<number>(0);
  const [averagePayPerLoad, setAveragePayPerLoad] = useState<number>(0);

  type UserNoteAttachment = {
    AttachmentID: number;
    Title: string;
  };

  type UserNote = {
    UserPerformanceNoteID: number;
    ModifiedByFullName: string;
    ModifiedDateTime: string | Date;
    Note: string;
    Attachments: UserNoteAttachment[];
  };

  const [notes, setNotes] = useState<UserNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [editNote, setEditNote] = useState<NewUserPerformanceNote | null>(null);

  const fetchDashboard = useCallback(() => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    const data = {
      UserId: parseInt(userIdParam),
      VehicleTypeIDs: vehicleTypeIds,
      BillingCustomerID: billingCustomer?.CustomerID,
      FromDate: fromDate,
      ToDate: toDate,
    };
    fetchApi(`/api/User/UserDashboard/`, data, 'POST')
      .then((response: UserDashboardResponse) => {
        setName(response.FullName);
        setBooked(response.Booked.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setMarginPercentage(response.MarginPercentage.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setConversionPercentage(response.ConversionPercentage.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setQuotes(response.Quotes.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setCustomerTouches(response.CustomerTouches.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setOrderRPM(response.OrderRPM.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setQuoteRPM(response.QuoteRPM.map((item) => ({
          ...item,
          Date: new Date(item.Date),
        })));
        setTripPayTotal(response.TripPayTotal);
        setAveragePayPerLoad(response.AveragePayPerLoad);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error fetching user dashboard');
      })
      .finally(() => setLoading(false));
  }, [userIdParam, vehicleTypeIds, billingCustomer?.CustomerID, fromDate, toDate]);

  const fetchNotes = useCallback(() => {
    setNotesLoading(true);
    fetchApi(`/api/User/UserPerformanceNotes/${userIdParam}`)
      .then((response: { Notes: UserNote[] }) => {
        setNotes(response.Notes);
      })
      .catch((e) => {
        if (!e?.status) alert('Error fetching notes');
      })
      .finally(() => setNotesLoading(false));
  }, [userIdParam]);

  useEffect(() => {
    fetchDashboard();
    fetchNotes();
  }, [fetchDashboard, fetchNotes]);

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  return <React.Fragment>
    <PopupNavBar />
    <Title string={name || 'User Dashboard'} />
    {editUserId && <UserPerformanceGoalDialog userId={editUserId} close={() => setEditUserId(null)} refresh={fetchDashboard} />}
    {editScheduleUserId && <UserWorkScheduleDialog userId={editScheduleUserId} close={() => setEditScheduleUserId(null)} refresh={noop} />}
    <nav className="navbar navbar-dark bg-dark">
      <Link to="/UserReports" className="navbar-brand">{name || 'USER DASHBOARD'} {loading && <Loader type="pulsing" className="p-0" />}</Link>
      <div className="d-flex align-items-center ml-auto">
        <Button
          title="Edit User"
          icon="edit"
          svgIcon={pencilIcon}
          disabled={loading}
          onClick={() => setEditUserId(parseInt(userIdParam))}
        />
        <Button
          title="Edit Schedule"
          icon="calendar"
          svgIcon={calendarIcon}
          disabled={loading}
          onClick={() => setEditScheduleUserId(parseInt(userIdParam))}
        />
      </div>
    </nav>
    <div className="container-fluid">
      <div className="row pt-2">
        <div className="col-md-4">
          <SimpleDateRangePicker
            inputProps={{ disabled: loading }}
            value={{ start: fromDate, end: toDate }}
            onChange={(e) => {
              setFromDate(e.start);
              setToDate(e.end);
            }}
          />
        </div>
        <div className="col-md-3">
          <MultiSelect
            placeholder="Select Vehicle Types"
            disabled={loading}
            data={VehicleTypes}
            textField="Name"
            dataItemKey="ID"
            value={VehicleTypes.filter(x => vehicleTypeIds.includes(x.ID))}
            onChange={(e) => setVehicleTypeIDs((e.value as any[]).map(x => x.ID))}
          />
        </div>
        <div className="col-md-2 input-group">
          <CustomerInput
            placeholder="Select Billing Customer"
            CustomerID={billingCustomer?.CustomerID}
            CustomerNumber={billingCustomer?.CustomerNumber}
            onChange={(customer) => setBillingCustomer(customer)}
          />
        </div>
      </div>
      <div className="row" style={{ marginTop: 20, marginBottom: 30 }}>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Quotes</div>
            <div className="panel-body">
              <h4>{quotes.length ? formatNumber(quotes.reduce((acc, q) => acc + q.Value, 0), 'n0') : '-'}</h4>
              {quotes.length > 1 && <Sparkline data={downsample(quotes, 28, o => o.Value)} style={{ height: 30 }} />}
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Customer Count</div>
            <div className="panel-body">
              <h4>{customerTouches.length ? (customerTouches.reduce((acc, c) => acc + c.Value, 0) / customerTouches.length).toFixed(1) + " avg" : '-'}</h4>
              {customerTouches.length > 1 && <Sparkline data={downsample(customerTouches, 28, o => o.Value)} style={{ height: 30 }} />}
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Order RPM</div>
            <div className="panel-body">
              <h4>{orderRPM.length ? formatNumber(orderRPM[orderRPM.length - 1].Value, 'c') : '-'}</h4>
              {orderRPM.length > 1 && <Sparkline data={downsample(orderRPM, 28, o => o.Value)} style={{ height: 30 }}><ChartTooltip shared={true} format="{0:c}" /></Sparkline>}
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Quote RPM</div>
            <div className="panel-body">
              <h4>{quoteRPM.length ? formatNumber(quoteRPM[quoteRPM.length - 1].Value, 'c') : '-'}</h4>
              {quoteRPM.length > 1 && <Sparkline data={downsample(quoteRPM, 28, q => q.Value)} style={{ height: 30 }}><ChartTooltip shared={true} format="{0:c}" /></Sparkline>}
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Total Trip Pay</div>
            <div className="panel-body">
              <h4 className="mb-1">{tripPayTotal ? formatNumber(tripPayTotal, 'c') : '-'}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="panel panel-default text-center">
            <div className="panel-heading">Avg Pay / Load</div>
            <div className="panel-body">
              <h4 className="mb-1">{averagePayPerLoad ? formatNumber(averagePayPerLoad, 'c') : '-'}</h4>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-xl-6">
          <Chart transitions={false}>
            <ChartTitle text="Orders Booked" />
            <ChartSeries>
              <ChartSeriesItem type="line" name="Booked" style="smooth" field="Value" categoryField="Date" data={booked} />
              <ChartSeriesItem type="linearTrendline" name="Linear Trendline" for="Booked" />
            </ChartSeries>
            <ChartCategoryAxis>
              <ChartCategoryAxisItem labels={{ step: booked.length > 10 ? Math.ceil(booked.length / 10) : 1 }} />
            </ChartCategoryAxis>
            <ChartTooltip shared={true} format="{0:n0}" />
            <ChartLegend visible={false} />
          </Chart>
        </div>
        <div className="col-xl-6">
          <Chart transitions={false}>
            <ChartTitle text="Margin %" />
            <ChartSeries>
              <ChartSeriesItem type="line" name="Margin %" style="smooth" field="Value" categoryField="Date" data={marginPercentage} />
              <ChartSeriesItem type="linearTrendline" name="Linear Trendline" for="Margin %" />
            </ChartSeries>
            <ChartCategoryAxis>
              <ChartCategoryAxisItem labels={{ step: marginPercentage.length > 10 ? Math.ceil(marginPercentage.length / 10) : 1 }} />
            </ChartCategoryAxis>
            <ChartValueAxis>
              <ChartValueAxisItem labels={{ format: "{0:p}" }} />
            </ChartValueAxis>
            <ChartTooltip shared={true} format="{0:p1}" />
            <ChartLegend visible={false} />
          </Chart>
        </div>
        <div className="col-xl-6">
          <Chart transitions={false}>
            <ChartTitle text="Conversion %" />
            <ChartSeries>
              <ChartSeriesItem type="line" name="Conversion %" style="smooth" field="Value" categoryField="Date" data={conversionPercentage} />
              <ChartSeriesItem type="linearTrendline" name="Linear Trendline" for="Conversion %" />
            </ChartSeries>
            <ChartCategoryAxis>
              <ChartCategoryAxisItem labels={{ step: conversionPercentage.length > 10 ? Math.ceil(conversionPercentage.length / 10) : 1 }}  />
            </ChartCategoryAxis>
            <ChartValueAxis>
              <ChartValueAxisItem labels={{ format: "{0:p}" }} />
            </ChartValueAxis>
            <ChartTooltip shared={true} format="{0:p1}" />
            <ChartLegend visible={false} />
          </Chart>
        </div>
      </div>
      <p className="text-center text-muted my-3">
        LOAD1, ANC, TEST, and TRANS1 are excluded from these results.<br />
        Available, In-Progress, Completed, and Dry Run orders are included in these results.
      </p>

      <hr />
      <div className="my-3">
        <h4>Performance Notes</h4>
        <Button themeColor="primary" className="mb-2" onClick={() => setEditNote({ Note: '' })}>Add Note</Button>
        {editNote && (
          <UserNoteEditUI
            loading={notesLoading}
            cancel={() => { setEditNote(null); fetchNotes(); }}
            save={(fd) => {
              setNotesLoading(true);
              fetchApiFormData(`/api/User/AddUserPerformanceNote/${userIdParam}`, fd, 'POST')
                .then(() => {
                  setEditNote(null);
                  fetchNotes();
                })
                .catch(() => alert('Error saving note'))
                .finally(() => setNotesLoading(false));
            }}
          />
        )}
        {notesLoading && <Loader type="converging-spinner" />}
        {notes.map((n) => (
          <div key={n.UserPerformanceNoteID} className="card mb-2">
            <div className="card-body">
              <h5 className="card-title mb-1">{n.ModifiedByFullName}</h5>
              <h6 className="card-subtitle mb-2 text-muted">{dayjs.utc(n.ModifiedDateTime).local().format('MM/DD/YYYY HH:mm')}</h6>
              <p className="card-text">{n.Note}</p>
              {n.Attachments.length > 0 && (
                <ul className="list-unstyled">
                  {n.Attachments.map((d) => (
                    <li key={d.AttachmentID}>
                      <a href={`/api/User/DownloadUserPerformanceNoteAttachment/${d.AttachmentID}`} target="_blank" rel="noopener noreferrer">{d.Title}</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </React.Fragment>;
}

export default UserReport;